import type {
  AlertProps,
  BadgeProps,
  ImageProps,
  StackProps,
  TextProps,
} from "@chakra-ui/react";
import { Card, CardBody, CardProps, useDisclosure } from "@chakra-ui/react";
import {
  ComponentTabItem,
  ComponentTabs,
  DetailDrawer,
  Image,
} from "@picovico/ui";
import { ReactNode, useState } from "react";

import type {
  ComponentTabsExtraProps,
  ComponentTabsProps,
  MovieCardDetailAction as MovieCardDetailActionProps,
} from "@picovico/ui";
import { MobileVerificationForm } from "components/Forms";
import { useWebFeature } from "@/hooks/useWebFeature";
import { useAppSelector } from "store";
import MembershipPoints, { MembershipPointsTabProps } from "./MembershipPoints";
import TicketHistory, { TicketHistoryTabProps } from "./TicketHistory";
import TicketPurchased from "./TicketPurchased";
import TicketReserved from "./TicketReserved";

export interface ProfileStatTabsProps {
  profileStatTabsProps?: CardProps;
  movieDetailDrawerLabelTextProps?: TextProps;
  movieCardDetailActionProps?: Partial<
    MovieCardDetailActionProps & CardProps
  > & {
    imageProps?: ImageProps;
    cardBodyProps?: StackProps;
    cardHallScreenTextProps?: TextProps;
    cardDateShowTimeTextProps?: TextProps;
    cardSeatsTextProps?: TextProps;
    cardPaymentStatusBadgeProps?: BadgeProps;
  };
  alertMessageProps?: AlertProps;
  ticketHistoryTabProps?: TicketHistoryTabProps;
  membershipTabProps?: MembershipPointsTabProps["membershipTabProps"];
  showMembershipTab?: boolean;
  tabProps?: ComponentTabsProps & ComponentTabsExtraProps;
}

export interface ProfileStatTabProps {
  openDetail: (data: ProfileDrawerData) => void;
  movieCardDetailActionProps?: ProfileStatTabsProps["movieCardDetailActionProps"];
  alertMessageProps?: AlertProps;
}

export type ProfileDrawerData = {
  image: string;
  data: Record<string, string | number | ReactNode | any>;
  actionButtons: ReactNode | Iterable<ReactNode>;
};

function ProfileStatTabs({
  profileStatTabsProps,
  movieCardDetailActionProps,
  movieDetailDrawerLabelTextProps,
  alertMessageProps,
  ticketHistoryTabProps,
  membershipTabProps,
  showMembershipTab = true,
  tabProps,
  ...props
}: ProfileStatTabsProps) {
  console.log(showMembershipTab);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const membershipEnabled = useWebFeature("membership");
  const user = useAppSelector((state) => state.user.user);
  const loyalty_config = useAppSelector(
    (state) => state.config.loyalty_config,
  ) || { enable: false };

  const [activeDrawerData, setActiveDrawerData] = useState<ProfileDrawerData>({
    image: "",
    data: {},
    actionButtons: <></>,
  });

  const handleOpenDetail = (data: ProfileDrawerData) => {
    setActiveDrawerData(data);
    onOpen();
  };

  const tabsList: ComponentTabItem[] = [
    {
      title: "My Reservations",
      component: TicketReserved,
      componentProps: {
        openDetail: handleOpenDetail,
        movieCardDetailActionProps,
        alertMessageProps,
      },
    },
    {
      title: "My Tickets",
      component: TicketPurchased,
      componentProps: {
        openDetail: handleOpenDetail,
        movieCardDetailActionProps,
        alertMessageProps,
      },
    },
    {
      title: "My History",
      component: TicketHistory,
      componentProps: {
        ticketHistoryTabProps,
        openDetail: handleOpenDetail,
      },
    },
  ];

  // The loyalty config AND the company feature: either off hides the tab.
  if (loyalty_config.enable && membershipEnabled && showMembershipTab) {
    tabsList.push({
      title: "My Membership",
      component: user?.is_verified ? (
        <MembershipPoints membershipTabProps={membershipTabProps} />
      ) : (
        <MobileVerificationForm />
      ),
      componentProps: {
        openDetail: handleOpenDetail,
      },
    });
  }

  return (
    <Card h="full" {...profileStatTabsProps}>
      <CardBody
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: {
            base: "20px 0",
            md: "20px",
          },
        }}
      >
        <ComponentTabs
          tabPanelProps={{
            height: "95%",
          }}
          tabList={tabsList}
          {...tabProps}
        />
      </CardBody>
      <DetailDrawer
        size="sm"
        movieDetailDrawerLabelTextProps={movieDetailDrawerLabelTextProps}
        isOpen={isOpen}
        title="Movie Detail"
        onClose={onClose}
        labeledListItem={activeDrawerData?.data || {}}
        showListBodyItems
        actionButtons={activeDrawerData?.actionButtons || <></>}
        drawerBodyHead={
          <Image
            w="full"
            maxH="30rem"
            objectFit="cover"
            src={activeDrawerData?.image as string}
            alt={(activeDrawerData?.data?.movie as string) || "Movie"}
          />
        }
        {...props}
      />
    </Card>
  );
}

export default ProfileStatTabs;
